import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Dialog} from 'primereact/dialog';
import {Button} from 'primereact/button';
import {HierarchicalIconclass} from '../types/HierarchicalIconclass';
import iconclassesService from '../services/iconclassesService';
import {handleError} from '../../../common/utils/errorHandling';
import {AdminButton, AdminInputDropdown, AdminInputRadio, AdminInputText} from '../../../layout/admin';
import IconclassItemTemplate from './IconclassItemTemplate';

type InputMode = 'list' | 'manual';

interface IconclassSelectionProps {
  selectedIconclasses: string[];
  onIconclassesChange: (iconclasses: string[]) => void;
}

const IconclassSelection: React.FC<IconclassSelectionProps> = ({
                                                                 selectedIconclasses,
                                                                 onIconclassesChange
                                                               }) => {
  const {t} = useTranslation();
  const [allIconclasses, setAllIconclasses] = useState<HierarchicalIconclass[]>([]);
  const [resolvedKeys, setResolvedKeys] = useState<HierarchicalIconclass[]>([]);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [selectedCode, setSelectedCode] = useState('');
  const [manualCode, setManualCode] = useState('');
  const [inputMode, setInputMode] = useState<InputMode>('list');
  const [expandedKeys, setExpandedKeys] = useState<HierarchicalIconclass[]>([]);
  const [selectedKeyCode, setSelectedKeyCode] = useState('');

  useEffect(() => {
    fetchAllIconclasses();
  }, []);

  useEffect(() => {
    fetchSelectedKeys();
  }, [selectedIconclasses]);

  useEffect(() => {
    setExpandedKeys([]);
    setSelectedKeyCode('');
  }, [selectedCode]);

  const fetchAllIconclasses = async (): Promise<void> => {
    await handleError(
      async () => {
        const iconclassesData = await iconclassesService.getHierarchicalIconclasses();
        setAllIconclasses(iconclassesData);
      },
      t('admin.artisticObjects.errors.fetchingIconclasses')
    );
  };

  const fetchSelectedKeys = async (): Promise<void> => {
    const resolvedCodes = new Set(resolvedKeys.map(ic => ic.code));
    const keyCodes = selectedIconclasses.filter(
      code => code.includes('(+') && !resolvedCodes.has(code)
    );
    if (keyCodes.length === 0) return;
    await handleError(
      async () => {
        const resolved = await iconclassesService.resolveIconclasses(keyCodes);
        setResolvedKeys(prev => {
          const existingCodes = new Set(prev.map(ic => ic.code));
          const newKeys = resolved.filter(ic => !existingCodes.has(ic.code));
          return newKeys.length > 0 ? [...prev, ...newKeys] : prev;
        });
      },
      t('admin.artisticObjects.errors.fetchingIconclasses')
    );
  };

  const handleExpandKeys = async (): Promise<void> => {
    if (!selectedCode) return;
    await handleError(
      async () => {
        const keys = await iconclassesService.expandKeys(selectedCode);
        setExpandedKeys(keys);
      },
      t('admin.artisticObjects.errors.expandingKeys')
    );
  };

  const openDialog = (): void => {
    setSelectedCode('');
    setManualCode('');
    setInputMode('list');
    setExpandedKeys([]);
    setSelectedKeyCode('');
    setDialogVisible(true);
  };

  const handleDialogHide = (): void => {
    setDialogVisible(false);
    setSelectedCode('');
    setManualCode('');
    setInputMode('list');
    setExpandedKeys([]);
    setSelectedKeyCode('');
  };

  const getCodeToAdd = (): string => {
    if (inputMode === 'manual') return manualCode.trim();
    return selectedKeyCode || selectedCode;
  };

  const isValidSubmission = (): boolean => {
    const code = getCodeToAdd();
    return code.length > 0 && !selectedIconclasses.includes(code);
  };

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    const code = getCodeToAdd();
    if (!code || selectedIconclasses.includes(code)) return;

    onIconclassesChange([...selectedIconclasses, code]);
    handleDialogHide();
    resolveKeyIfMissing(code);
  };

  const resolveKeyIfMissing = (code: string): void => {
    if (code.includes('(+') && !resolvedKeys.find(ic => ic.code === code)) {
      iconclassesService.resolveIconclasses([code]).then(resolved => {
        if (resolved.length > 0) {
          setResolvedKeys(prev => [...prev, ...resolved]);
        }
      });
    }
  };

  const handleDelete = (code: string): void => {
    onIconclassesChange(selectedIconclasses.filter(c => c !== code));
  };

  const getIconclass = (code: string): HierarchicalIconclass | undefined => {
    return allIconclasses.find(ic => ic.code === code)
      || resolvedKeys.find(ic => ic.code === code);
  };

  const availableOptions = allIconclasses
    .filter(ic => !selectedIconclasses.includes(ic.code))
    .map(iconclass => ({
      label: `${iconclass.code} - ${iconclass.fullName.join(' > ')}`,
      value: iconclass.code
    }));

  return (
    <>
      {selectedIconclasses.length > 0 && (
        <div className="mb-4 space-y-2">
          {selectedIconclasses.map((code) => {
            const iconclass = getIconclass(code);
            return (
              <div key={code} className="flex items-center justify-between p-3 bg-gray-50 rounded border">
                <div className="flex-1">
                  {iconclass ? (
                    <IconclassItemTemplate iconclass={iconclass}/>
                  ) : (
                    <span className="font-medium">{code}</span>
                  )}
                </div>
                <Button
                  className="hover:text-gray-700"
                  type="button"
                  icon="fa fa-trash"
                  size="small"
                  severity="danger"
                  outlined
                  onClick={() => handleDelete(code)}
                />
              </div>
            );
          })}
        </div>
      )}

      <AdminButton
        label={t('admin.artisticObjects.actions.addIconclass')}
        onClick={openDialog}
        variant="grey"
      />

      <Dialog
        header={t('admin.artisticObjects.dialog.addIconclassTitle')}
        visible={dialogVisible}
        style={{width: '50vw'}}
        onHide={handleDialogHide}
        maskClassName="bg-black bg-opacity-50"
        blockScroll
      >
        <form>
          <AdminInputRadio
            label={t('admin.artisticObjects.fields.inputMode')}
            value={inputMode}
            onChange={(value) => setInputMode(value as InputMode)}
            options={[
              {label: t('admin.artisticObjects.inputModes.selectFromList'), value: 'list'},
              {label: t('admin.artisticObjects.inputModes.enterManually'), value: 'manual'}
            ]}
            name="inputMode"
            id="inputMode"
            className="mb-4"
          />

          {inputMode === 'list' && (
            <>
              <AdminInputDropdown
                label={t('admin.artisticObjects.fields.iconclass')}
                value={selectedCode}
                onChange={(e) => setSelectedCode(e.target.value)}
                options={availableOptions}
                placeholder={t('admin.artisticObjects.placeholders.selectIconclass')}
                name="iconclassCode"
                id="iconclassCode"
                required
                className="mb-4"
              />
              {selectedCode && (getIconclass(selectedCode)?.directKeysCount ?? 0) > 0 && expandedKeys.length === 0 && (
                <div className="mb-4 -mt-2 flex items-center gap-1 text-sm text-gray-600">
                  <span>+{getIconclass(selectedCode)?.directKeysCount} {t('admin.artisticObjects.expandKeys.label')}</span>
                  <button
                    type="button"
                    className="hover:text-gray-900 flex items-center gap-1"
                    onClick={handleExpandKeys}
                  >
                    <i className="fa fa-circle-chevron-right"/>
                    <span>{t('admin.artisticObjects.expandKeys.expand')}</span>
                  </button>
                </div>
              )}
              {expandedKeys.length > 0 && (
                <AdminInputDropdown
                  label={t('admin.artisticObjects.fields.key')}
                  value={selectedKeyCode}
                  onChange={(e) => setSelectedKeyCode(e.target.value)}
                  options={expandedKeys.map(ic => ({
                    label: `${ic.code} - ${ic.fullName.join(' > ')}`,
                    value: ic.code
                  }))}
                  placeholder={t('admin.artisticObjects.expandKeys.selectKey')}
                  name="keyCode"
                  id="keyCode"
                  className="mb-4 -mt-2"
                />
              )}
            </>
          )}

          {inputMode === 'manual' && (
            <AdminInputText
              label={t('admin.artisticObjects.fields.iconclassCode')}
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              placeholder={t('admin.artisticObjects.placeholders.enterIconclassCode')}
              name="manualIconclassCode"
              id="manualIconclassCode"
              required
            />
          )}

          <div className="flex justify-end gap-4 pt-4">
            <AdminButton
              type="button"
              label={t('admin.common.actions.cancel')}
              onClick={handleDialogHide}
              variant="white"
            />
            <AdminButton
              type="submit"
              label={t('admin.common.actions.add')}
              variant="black"
              onClick={handleSubmit}
              disabled={!isValidSubmission()}
            />
          </div>
        </form>
      </Dialog>
    </>
  );
};

export default IconclassSelection;

